"use client";

import {
  Sidebar,
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { useEntitlements } from "@/app/providers/entitlements-provider";
import { Perms, useBulkPermissions } from "@/hooks/use-permissions";
import { permissionKey } from "@/lib/permission-checks";
import { cn } from "@/utils/utils";
import { ShopSummary } from "@/types/shop";
import { ChevronLeft } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import * as React from "react";
import { useShopStore } from "@/store/shopStore";
import { AppShellSidebar } from "./sidebar/app-shell-sidebar";
import { isNavItemActive } from "./sidebar/find-active-nav-item";
import { settingsSideBarData } from "./sidebar/settings-nav-data";
import { SidebarNavIcon } from "./sidebar/sidebar-nav-icons";
import { getEditorHasUnsavedChanges } from "@/utils/editor-unsaved-guard";

interface Props extends React.ComponentProps<typeof Sidebar> {
  shop?: ShopSummary;
}

export { settingsSideBarData } from "./sidebar/settings-nav-data";

export const SettingsSidebar = ({ shop: _shop, ...props }: Props) => {
  const pathname = usePathname();
  const { shopType } = useShopStore();
  const { isMobile, setOpenMobile } = useSidebar();
  const entitlements = useEntitlements();

  const { allowed: allowedMap } = useBulkPermissions(
    settingsSideBarData
      .map((item) => item.permissions)
      .filter((item): item is Perms => item != null),
  );

  const filteredItems = settingsSideBarData.filter((item) => {
    if (item.shopType && item.shopType !== shopType) {
      return false;
    }

    if (
      item.entitlement &&
      !entitlements.hasAdminAccess &&
      !entitlements[item.entitlement].hasAccess
    ) {
      return false;
    }

    if (!item.permissions) {
      return true;
    }
    return allowedMap.get(permissionKey(item.permissions)) ?? true;
  });

  const closeMobileSidebar = () => {
    if (isMobile && !getEditorHasUnsavedChanges()) {
      setOpenMobile(false);
    }
  };

  return (
    <AppShellSidebar
      {...props}
      id="settings-sidebar"
      className={cn(props.className)}
      header={
        <div className="flex h-[var(--header-height)] w-full items-center gap-1 pl-1">
          <Link
            href="/home"
            aria-label="Go back"
            className="flex size-7 shrink-0 items-center justify-center rounded-md text-sidebar-foreground hover:bg-black/[0.03]"
            onClick={closeMobileSidebar}
          >
            <ChevronLeft className="size-4" />
          </Link>
          <span className="text-sm font-medium text-sidebar-foreground">
            Settings
          </span>
        </div>
      }
    >
      <SidebarGroup>
        <SidebarMenu>
          {filteredItems.map((item) => (
            <SidebarMenuItem key={item.title}>
              <SidebarMenuButton
                asChild
                isActive={isNavItemActive(pathname, item.url)}
                tooltip={item.title}
              >
                <Link href={item.url} onClick={closeMobileSidebar}>
                  {item.icon && (
                    <SidebarNavIcon icon={item.icon} className="size-4" />
                  )}
                  <span data-sidebar="menu-label">{item.title}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarGroup>
    </AppShellSidebar>
  );
};
